import i18nFactory from '@yandex-int/i18n';
import { Action } from 'redux';
import { ofType } from 'redux-observable';
import { EMPTY, from, Observable } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';

import { dummyAction } from '../../actions/common';
import { NoticeType, showNoticeMessage } from '../../actions/grid-screen';
import {
    acceptMinusPhraseGroupsMassActions,
    OPEN_MINUS_PHRASE_GROUPS_MASS_ACTIONS
} from '../../actions/groups-edit/groups-edit-mass-actions';
import {
    ACCEPT_LIBRARY_LIST_EDITOR_DATA,
    AcceptLibraryListEditorDataAction,
    closeModalAddLibraryList,
    FETCH_LIBRARY_LISTS_FOR_EDIT,
    fetchLibraryListsForEdit,
    FetchLibraryListsForEditAction,
    getLibraryLists,
    OPEN_MINUS_PHRASES_EDITOR,
    OpenMinusPhrasesEditor,
    setAddedLibraryList,
    showErrorLibraryListEditor,
    showGetLibraryListsError
} from '../../actions/minusPhrasesEditor';
import { FetchMinusPhraseLibraryDataResult } from '../../api/MinusPhraseLibraryProvider/MinusPhraseLibraryProvider';
import { MinusPhraseMutationResultData } from '../../api/MinusPhraseLibraryProvider/MinusPhraseMutationExecutor';
import {
    formatMinusPhrasesToApi,
    mapValidationResultItemToErrorData,
    validateMinusPhraseData
} from '../../mappers/minus-phrase/minus-phrase';
import { mapFetchResultToLibraryItem } from '../../mappers/minusPhrases';
import { selectMinusPhrasesLibraryConstants } from '../../selectors/minusPhraseLibrary/minusPhraseLibrary';
import { selectGroupLibraryListEditorInfo } from '../../selectors/minusPhrases/minusPhrases';
import { ApiMethodExecutingError } from '../../service/Errors';
import { DnaEpic } from '../../typings/epic';
import { addMinusKeywordsPacks } from '../../typings/graphql-grid';
import { MassActionId } from '../../typings/groupsEditState';

import * as keyset from './minusPhrasesEditor.i18n';

const i18n = i18nFactory(keyset);

export const openEditorMinusPhraseLibraryEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(
            OPEN_MINUS_PHRASES_EDITOR,
            OPEN_MINUS_PHRASE_GROUPS_MASS_ACTIONS
        ),
        filter(() => {
            return (
                Boolean(
                    store$.value.commonData.features.isMinusWordsLibEnabled
                ) &&
                Boolean(
                    !store$.value.minusPhraseLibraryEditor.isLibraryListsFetched
                )
            );
        }),
        map((action: OpenMinusPhrasesEditor) => {
            return fetchLibraryListsForEdit(action.editorId);
        })
    );
};

export const fetchLibraryListsEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(FETCH_LIBRARY_LISTS_FOR_EDIT),
        mergeMap(
            (action: FetchLibraryListsForEditAction): Observable<Action> => {
                const offset = 0;
                const constants = store$.value.commonData.constants;

                if (!constants) {
                    return EMPTY;
                }

                const limit =
                    constants.minusPhraseLibraryConstants.maxLibraryPacksCount;

                return from(
                    deps.minusPhraseLibraryDataProvider.fetchMinusPhraseLibraryData(
                        offset,
                        limit
                    )
                ).pipe(
                    mergeMap(
                        (
                            response: FetchMinusPhraseLibraryDataResult
                        ): Array<Action> => {
                            const result: Array<Action> = [
                                getLibraryLists(
                                    action.editorId,
                                    mapFetchResultToLibraryItem(response.data)
                                )
                            ];

                            if (action.addedLibraryList) {
                                const maxLinkedPacksToOneAdGroup =
                                    constants.minusPhraseLibraryConstants
                                        .maxLinkedPacksToOneAdGroup;

                                return [
                                    ...result,
                                    setAddedLibraryList(
                                        action.editorId,
                                        action.addedLibraryList,
                                        maxLinkedPacksToOneAdGroup
                                    )
                                ];
                            }

                            if (
                                store$.value.minusPhraseLibraryEditor
                                    .libraryItemsIsUpdating
                            ) {
                                if (
                                    action.editorId === MassActionId.groupsEdit
                                ) {
                                    result.push(
                                        acceptMinusPhraseGroupsMassActions(
                                            action.editorId
                                        )
                                    );
                                }

                                return result;
                            }

                            return result;
                        }
                    ),
                    catchError(() => {
                        return [showGetLibraryListsError(action.editorId)];
                    })
                );
            }
        )
    );
};

export const addLibraryListEditorEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ACCEPT_LIBRARY_LIST_EDITOR_DATA),
        filter(() => {
            return Boolean(
                store$.value.commonData.features.isMinusWordsLibEnabled
            );
        }),
        mergeMap(
            (action: AcceptLibraryListEditorDataAction): Observable<Action> => {
                const state = store$.value;

                const constants = selectMinusPhrasesLibraryConstants(state);
                const data = selectGroupLibraryListEditorInfo(
                    state,
                    action.editorId
                );

                if (!constants || !data) {
                    return EMPTY;
                }

                const listTitle = data.listTitle;
                const minusPhrases = data.minusPhrases;

                const clientValidationResult = validateMinusPhraseData(
                    {
                        title: listTitle,
                        minusPhrases
                    },
                    'addItems',
                    constants
                );

                if (clientValidationResult.length) {
                    return Observable.of(
                        showErrorLibraryListEditor(
                            action.editorId,
                            mapValidationResultItemToErrorData(
                                clientValidationResult
                            )
                        )
                    );
                }

                return Observable.fromPromise(
                    deps.minusPhraseLibraryMutationExecutor.addMinusKeywordsPack(
                        {
                            addItems: [
                                {
                                    name: listTitle,
                                    minusKeywords: formatMinusPhrasesToApi(
                                        minusPhrases
                                    )
                                }
                            ]
                        }
                    )
                ).pipe(
                    mergeMap(
                        (response: MinusPhraseMutationResultData): Action[] => {
                            const { validationResult } = response;

                            if (validationResult) {
                                return [
                                    showErrorLibraryListEditor(
                                        action.editorId,
                                        mapValidationResultItemToErrorData(
                                            validationResult
                                        )
                                    )
                                ];
                            }

                            showNoticeMessage(
                                NoticeType.success,
                                i18n('add-minus-keywords-pack-success')
                            );

                            const addedItems = (response.data as addMinusKeywordsPacks)
                                .addMinusKeywordsPacks.addedItems;
                            const addedItem = addedItems[0];

                            if (!addedItem) {
                                return [dummyAction];
                            }

                            const addedListId = String(addedItem.id);

                            return [
                                closeModalAddLibraryList(action.editorId),
                                fetchLibraryListsForEdit(
                                    action.editorId,
                                    addedListId
                                )
                            ];
                        }
                    ),
                    catchError((err: ApiMethodExecutingError) => {
                        return Observable.of(
                            showErrorLibraryListEditor(
                                action.editorId,
                                mapValidationResultItemToErrorData(
                                    err.getErrors()
                                )
                            )
                        );
                    })
                );
            }
        )
    );
};
